import React, { useEffect, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import PencilOutlineIcon from 'mdi-react/PencilOutlineIcon';
import EditDepartmentModal from '../modals/edit-department-modal.component';
import type Department from '../models/department/department';
import type DepartmentChangeData from '../models/department/department-change-data';
import type Room from '../models/room/room';
import * as styles from '../office-workspace.styles';

interface Props {
  readonly initialDepartment: Department;
  readonly rooms: Room[];
  readonly onDelete: (departmentChangeData: DepartmentChangeData) => void;
  readonly handleChange: (departmentChangeData: DepartmentChangeData) => void;
  readonly isNameEmptyOrInUse: (departmentChangeData: DepartmentChangeData) => boolean;
  /** Position in the departments list — decides the accent colour, shared with the rooms card. */
  readonly accentIndex: number;
}

const initials = (name: string): string => name.trim().slice(0, 2).toUpperCase();

const DepartmentCard = ({
  initialDepartment,
  rooms,
  onDelete,
  handleChange,
  isNameEmptyOrInUse,
  accentIndex,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const [showEditDepartmentModal, setShowEditDepartmentModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<Department>({
    ...initialDepartment,
  });

  useEffect(() => {
    setSelectedDepartment(initialDepartment);
  }, [initialDepartment]);

  const accent = styles.accentAt(accentIndex);
  const roomCount = selectedDepartment.rooms?.length ?? 0;

  return (
    <>
      <div
        className={style(styles.departmentRow)}
        role="button"
        tabIndex={0}
        onClick={(): void => setShowEditDepartmentModal(true)}
        onKeyDown={(event): void => {
          if (event.key === 'Enter' || event.key === ' ') {
            // Space would scroll the page as well as opening the modal.
            event.preventDefault();
            setShowEditDepartmentModal(true);
          }
        }}
      >
        <div className={style(styles.avatar(accent))}>{initials(selectedDepartment.name)}</div>
        <div className={style(styles.departmentName)}>
          {selectedDepartment.name}
          <span className={style(styles.departmentMeta)}>
            {roomCount} {roomCount === 1 ? t('room') : t('rooms')}
          </span>
        </div>
        <span className={style(styles.iconButton)} aria-hidden="true">
          <PencilOutlineIcon size={18} />
        </span>
      </div>
      <EditDepartmentModal
        showModal={showEditDepartmentModal}
        handleClose={(): void => {
          setShowEditDepartmentModal(false);
        }}
        department={selectedDepartment}
        rooms={rooms}
        handleSave={handleChange}
        onDelete={onDelete}
        nameIsEmptyOrInUse={isNameEmptyOrInUse}
      />
    </>
  );
};

export default DepartmentCard;
